import React, { useEffect, useState, useMemo } from "react";
import { contentAdminApi } from "../../../lib/api";
import { SiteHeader, SiteTabs } from "./SiteDashboard";
import { FieldStatusDot } from "./BlockStatusBadge";
import { Loader2, Save, RotateCcw, Search, Check } from "lucide-react";

export default function UIStringsEditor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({}); // {string_key: value or null}
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await contentAdminApi.listUiStrings();
        setRows(res.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const areas = useMemo(() => Array.from(new Set(rows.map((r) => r.area).filter(Boolean))).sort(), [rows]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (filter !== "all" && r.area !== filter) return false;
    if (q && !r.string_key.toLowerCase().includes(q.toLowerCase())
        && !r.label.toLowerCase().includes(q.toLowerCase())
        && !r.default_value.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, filter, q]);

  const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));
  const currentValue = (row) => {
    if (row.string_key in drafts) return drafts[row.string_key];
    return row.value === null || row.value === undefined ? "" : row.value;
  };
  const isDirty = (row) => row.string_key in drafts && drafts[row.string_key] !== (row.value ?? "");

  const save = async (row) => {
    const v = drafts[row.string_key];
    setSaving((s) => ({ ...s, [row.string_key]: true }));
    try {
      // Special-case: empty input → send null (revert to default). To send intentional empty,
      // user can use the "Nascondi" button (see below).
      const value = v === "" ? null : v;
      const res = await contentAdminApi.updateUiString(row.string_key, value);
      setRows((prev) => prev.map((r) => r.string_key === row.string_key ? res.data : r));
      setDrafts((d) => { const next = { ...d }; delete next[row.string_key]; return next; });
    } finally {
      setSaving((s) => ({ ...s, [row.string_key]: false }));
    }
  };

  const setHidden = async (row) => {
    setSaving((s) => ({ ...s, [row.string_key]: true }));
    try {
      const res = await contentAdminApi.updateUiString(row.string_key, "");
      setRows((prev) => prev.map((r) => r.string_key === row.string_key ? res.data : r));
      setDrafts((d) => { const next = { ...d }; delete next[row.string_key]; return next; });
    } finally {
      setSaving((s) => ({ ...s, [row.string_key]: false }));
    }
  };

  const revert = async (row) => {
    setSaving((s) => ({ ...s, [row.string_key]: true }));
    try {
      const res = await contentAdminApi.updateUiString(row.string_key, null);
      setRows((prev) => prev.map((r) => r.string_key === row.string_key ? res.data : r));
      setDrafts((d) => { const next = { ...d }; delete next[row.string_key]; return next; });
    } finally {
      setSaving((s) => ({ ...s, [row.string_key]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <SiteTabs activeKey="ui-strings" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Sito Web</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937]">Testi interfaccia</h1>
          <p className="text-slate-500 mt-1 text-sm">Etichette brevi di bottoni, sezioni e messaggi. Se vuoi tornare al testo di default, clicca &quot;Ripristina default&quot;.</p>
        </div>

        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per chiave, etichetta o testo…"
              className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 focus:border-[#12964A] outline-none text-sm"
              data-testid="ui-strings-search"
            />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border border-slate-200 text-sm px-3 py-2" data-testid="ui-strings-area-filter">
            <option value="all">Tutte le aree</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left px-4 py-2.5">Chiave · Etichetta</th>
                  <th className="text-left px-4 py-2.5 w-1/3">Valore personalizzato</th>
                  <th className="text-left px-4 py-2.5">Default (fallback)</th>
                  <th className="px-4 py-2.5 w-40">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const state = row.value === null ? "fallback" : "published";
                  return (
                    <tr key={row.string_key} className="border-t border-slate-100" data-testid={`ui-string-row-${row.string_key}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1.5">
                          <FieldStatusDot state={state} />
                          <span className="font-mono text-[11px] text-slate-500">{row.string_key}</span>
                        </div>
                        <div className="text-slate-800 mt-0.5">{row.label}</div>
                        {row.context && <div className="text-xs text-slate-400 mt-0.5">{row.context}</div>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <input
                          type="text"
                          value={currentValue(row)}
                          onChange={(e) => setDraft(row.string_key, e.target.value)}
                          placeholder={row.default_value}
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm"
                          data-testid={`ui-string-input-${row.string_key}`}
                        />
                        {row.value === "" && <div className="text-xs text-yellow-700 mt-1">⚠ Volutamente vuoto (nascosto)</div>}
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600 text-xs">
                        <span className="italic">{row.default_value}</span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => save(row)}
                            disabled={!isDirty(row) || saving[row.string_key]}
                            className="inline-flex items-center gap-1 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-2.5 py-1 text-xs"
                            data-testid={`ui-string-save-${row.string_key}`}
                          >
                            {saving[row.string_key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Salva
                          </button>
                          <button
                            onClick={() => revert(row)}
                            disabled={saving[row.string_key] || row.value === null}
                            className="inline-flex items-center gap-1 border border-slate-200 hover:border-slate-400 rounded-full px-2.5 py-1 text-xs"
                            title="Torna al valore di default"
                            data-testid={`ui-string-revert-${row.string_key}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-400">Nessun risultato per i filtri correnti.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
